/*
 * The event's own day, and how far away it is.
 *
 * Both answers must be computed in the **event's** timezone, not the viewer's and not UTC. A show
 * that ends at 2026-11-15T00:30+07:00 is a Thai event finishing just after midnight on the 15th;
 * read as UTC that is 17:30 on the 14th, and every screen would place it on the wrong day. The
 * event carries its own timezone for exactly this, and where it has none the viewer's is the least
 * wrong fallback — a Thai organizer's browser is already in Asia/Bangkok.
 */

/** `YYYY-MM-DD` as the date reads in `timezone`. Null for a value that is not a date. */
export function eventDayIn(iso: string | null, timezone: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    // `en-CA` formats as YYYY-MM-DD, which is the shape every other date in this console uses.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone ?? undefined,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(at);
  } catch {
    // An unknown timezone string is data, not a crash: fall back to the viewer's own day.
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
  }
}

export interface Countdown {
  /** Whole days from today to the event's day. Negative once it is past, 0 on the day itself. */
  days: number;
  /** The event's day as `YYYY-MM-DD`, for rendering beside the count. */
  day: string;
}

/**
 * Whole days between today and the event's day, both taken in the event's timezone.
 *
 * Counted in **days, not hours**: "อีก 3 วัน" is what an organizer plans around, and an hours-based
 * count would flip from 3 to 2 in the middle of an afternoon for no reason anyone can see.
 */
export function countdownTo(
  iso: string | null,
  timezone: string | null,
  now: Date = new Date(),
): Countdown | null {
  const day = eventDayIn(iso, timezone);
  const today = eventDayIn(now.toISOString(), timezone);
  if (!day || !today) return null;
  // Both are YYYY-MM-DD in the same zone, so comparing them as UTC midnights is exact.
  const diff = Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return { days: Math.round(diff / 86_400_000), day };
}

/** The countdown as a sentence, in the words the console uses elsewhere. */
export function countdownWords(countdown: Countdown | null): string {
  if (!countdown) return 'ยังไม่ได้ตั้งวันจบงาน';
  if (countdown.days > 1) return `อีก ${countdown.days} วัน`;
  if (countdown.days === 1) return 'พรุ่งนี้';
  if (countdown.days === 0) return 'วันนี้';
  if (countdown.days === -1) return 'เมื่อวาน';
  return `ผ่านมาแล้ว ${Math.abs(countdown.days)} วัน`;
}
